import { ReactNode } from 'react'
import { IconButton } from '../Button'
import { Tooltip } from 'antd'

export interface TableAction {
  /**
   * Unique key for the action
   */
  key: string

  /**
   * Icon to display
   */
  icon: ReactNode

  /**
   * Click handler
   */
  onClick: () => void

  /**
   * Accessible label for screen readers
   */
  ariaLabel: string

  /**
   * Tooltip text (defaults to ariaLabel)
   */
  title?: string

  /**
   * Disabled state
   */
  disabled?: boolean

  /**
   * Danger mode (for destructive actions like delete)
   */
  danger?: boolean
}

export interface TableActionsProps {
  /**
   * Array of actions to display
   */
  actions: TableAction[]

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * TableActions Component
 *
 * A reusable component for rendering action buttons in table cells.
 * Follows the design spec: 20px icons, 20px gap between icons.
 *
 * @example
 * <TableActions
 *   actions={[
 *     {
 *       key: 'qr',
 *       icon: <QrcodeOutlined />,
 *       onClick: handleQrClick,
 *       ariaLabel: 'View QR Code',
 *     },
 *     {
 *       key: 'edit',
 *       icon: <EditOutlined />,
 *       onClick: handleEdit,
 *       ariaLabel: 'Edit',
 *     },
 *     {
 *       key: 'delete',
 *       icon: <DeleteOutlined />,
 *       onClick: handleDelete,
 *       ariaLabel: 'Delete',
 *       danger: true,
 *     },
 *   ]}
 * />
 */
export function TableActions({ actions, className = '' }: TableActionsProps) {
  return (
    <div className={`table-actions ${className}`}>
      {actions.map((action) => {
        const button = (
          <IconButton
            key={action.key}
            icon={action.icon}
            onClick={action.onClick}
            ariaLabel={action.ariaLabel}
            title={action.title}
            disabled={action.disabled}
            danger={action.danger}
            className="table-actions-icon"
          />
        )

        return (
          <Tooltip key={action.key} title={action.title || action.ariaLabel}>
            <span>{button}</span>
          </Tooltip>
        )
      })}
    </div>
  )
}
